import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry, of, map } from 'rxjs';
import { environment } from '../../environments/environment';

// Interface สำหรับข้อมูลอ้างอิงผู้ใช้งาน (referenceUserData) ที่ได้จาก backend API
export interface ReferenceUserData {
  id: string;
  idCardNumber: string;      // เลขบัตรประชาชน (เก็บพร้อมเว้นวรรค เช่น "3 7001 00732 69 9")
  title: string;             // คำนำหน้า เช่น "นาย" / "นางสาว"
  name: string;              // ชื่อ
  lastname: string;          // นามสกุล
  idMember: string;          // รหัสสมาชิก เช่น "ส.41-1083"
  expirationDate: string | null; // วันหมดอายุ (free-text เช่น "ตลอดชีพ" หรือ "22 November 2568")
  createdAt: string;
  updatedAt: string;

  // ฟิลด์คำนวณฝั่ง client: ผู้ใช้รายนี้สมัครเข้าระบบแล้วหรือยัง (เทียบด้วย cid กับตาราง users)
  hasRegistered?: boolean;
}

// รูปแบบ response ของ GET /reference-user-data
export interface ReferenceUserDataListResponse {
  data: ReferenceUserData[];
  total: number;
}

// payload สำหรับแก้ไขข้อมูล (แก้ไขได้ทุกฟิลด์ รวมถึงเลขบัตรประชาชน)
export interface UpdateReferenceUserDataPayload {
  idCardNumber?: string;
  title?: string;
  name?: string;
  lastname?: string;
  idMember?: string;
  expirationDate?: string | null;
}

// ตัวกรองฝั่ง client
export interface ReferenceUserDataFilters {
  searchTerm?: string;    // ค้นหาทั่วไป (ชื่อ, นามสกุล, เลขบัตร, รหัสสมาชิก)
  idCardSearch?: string;  // ค้นหาด้วยเลขบัตรประชาชน
  memberIdSearch?: string; // ค้นหาด้วยรหัสสมาชิก
  expiration?: 'all' | 'lifetime' | 'dated' | 'none';
  registration?: 'all' | 'registered' | 'not-registered'; // สถานะการสมัคร
}

@Injectable({
  providedIn: 'root'
})
export class ReferenceUserDataService {
  private readonly API_URL = environment.apiUrl;
  private readonly REQUEST_TIMEOUT = 15000; // 15 seconds

  constructor(private http: HttpClient) {
    console.log('🌐 ReferenceUserDataService API URL:', this.API_URL);
  }

  /**
   * ดึงข้อมูล referenceUserData แบบแบ่งหน้าจาก backend
   * @param page เลขหน้า (เริ่มที่ 1)
   * @param limit จำนวนรายการต่อหน้า
   */
  getReferenceUserData(page = 1, limit = 10): Observable<ReferenceUserDataListResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    const url = `${this.API_URL}/reference-user-data`;
    console.log('🔄 ReferenceUserDataService: กำลังเรียกข้อมูลอ้างอิงผู้ใช้งาน...');
    console.log('🌐 API URL:', `${url}?page=${page}&limit=${limit}`);

    return this.http.get<ReferenceUserDataListResponse>(url, { params }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(1),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ ReferenceUserDataService: ไม่สามารถเชื่อมต่อ API:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * ตัดอักขระที่ไม่ใช่ตัวเลขออกจากเลขบัตรประชาชน เพื่อใช้เปรียบเทียบข้ามตาราง
   * - รองรับช่องว่าง/ขีด เช่น "3 7001 00732 69 9" -> "3700100732699"
   * - รองรับเลขไทย ๐-๙ -> 0-9
   */
  normalizeIdNumber(value: string | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }
    const thaiDigits = '๐๑๒๓๔๕๖๗๘๙';
    // แปลงเลขไทยเป็นเลขอารบิกก่อน แล้วตัดทุกตัวที่ไม่ใช่ตัวเลข
    const arabic = String(value).replace(/[๐-๙]/g, (d) => String(thaiDigits.indexOf(d)));
    return arabic.replace(/\D/g, '');
  }

  /**
   * ดึงรายการเลขบัตรประชาชน (cid) ของผู้ใช้ที่สมัครเข้าระบบแล้วจากตาราง users
   * คืนค่าเป็น Set ของ cid ที่ normalize แล้ว เพื่อใช้เทียบแบบ O(1)
   *
   * ใช้ /users/full-details (endpoint เดียวกับหน้า user-management ที่ใช้งานได้จริง)
   * และ parse แบบยืดหยุ่น (รองรับทั้ง array ตรงๆ และแบบห่อด้วย { data: [...] })
   * หากเรียกไม่สำเร็จจะคืน Set ว่าง เพื่อให้หน้าจอยังทำงานได้
   */
  getRegisteredCidSet(): Observable<Set<string>> {
    const url = `${this.API_URL}/users/full-details`;
    console.log('🔄 ReferenceUserDataService: กำลังดึงรายชื่อผู้ใช้ที่สมัครแล้ว...', url);

    return this.http.get<any>(url).pipe(
      timeout(this.REQUEST_TIMEOUT),
      map((res) => this.buildCidSet(res)),
      catchError((error: HttpErrorResponse) => {
        console.error(
          '❌ ReferenceUserDataService: ดึง /users/full-details ไม่สำเร็จ:',
          error.status,
          error.message
        );
        // fallback: ลอง endpoint /users (แบบเบา) เผื่อ full-details มีปัญหา
        return this.http.get<any>(`${this.API_URL}/users`).pipe(
          timeout(this.REQUEST_TIMEOUT),
          map((res) => this.buildCidSet(res, 'fallback /users')),
          catchError((err: HttpErrorResponse) => {
            console.error('❌ ReferenceUserDataService: fallback /users ก็ไม่สำเร็จ:', err.status, err.message);
            return of(new Set<string>());
          })
        );
      })
    );
  }

  /**
   * สร้าง Set ของ cid ที่ normalize แล้วจาก response ของ users (parse แบบยืดหยุ่น)
   */
  private buildCidSet(res: any, sourceLabel = '/users/full-details'): Set<string> {
    // รองรับทั้ง array ตรงๆ, { data: [...] }, { users: [...] }
    const users: any[] = Array.isArray(res)
      ? res
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.users)
          ? res.users
          : [];

    const set = new Set<string>();
    const samples: string[] = [];

    users.forEach((u) => {
      // cid อาจอยู่หลายตำแหน่งขึ้นกับ endpoint
      const rawCid = u?.cid ?? u?.userInformation?.cid ?? u?.user?.cid ?? '';
      const normalized = this.normalizeIdNumber(rawCid);
      if (normalized) {
        set.add(normalized);
        if (samples.length < 5) {
          samples.push(`"${rawCid}" -> "${normalized}"`);
        }
      }
    });

    console.log(
      `✅ [registered] source=${sourceLabel} usersFetched=${users.length} uniqueCid=${set.size}`
    );
    console.log('🔎 [registered] ตัวอย่าง cid ที่สมัครแล้ว:', samples);
    return set;
  }

  /**
   * ค้นหาข้อมูลอ้างอิงด้วยเลขบัตรประชาชน (backend จะตัดช่องว่างให้อัตโนมัติ)
   */
  getByIdCardNumber(idCardNumber: string): Observable<ReferenceUserData> {
    const url = `${this.API_URL}/reference-user-data/by-id-card/${encodeURIComponent(idCardNumber)}`;
    return this.http.get<ReferenceUserData>(url).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ ReferenceUserDataService: ค้นหาด้วยเลขบัตรไม่สำเร็จ:', error.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * สร้างข้อมูลอ้างอิงผู้ใช้งานรายการใหม่ (POST /reference-user-data)
   */
  createReferenceUserData(
    payload: UpdateReferenceUserDataPayload
  ): Observable<ReferenceUserData> {
    const url = `${this.API_URL}/reference-user-data`;
    console.log('🔄 ReferenceUserDataService: กำลังสร้างข้อมูลใหม่...', payload);

    return this.http.post<ReferenceUserData>(url, payload).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ ReferenceUserDataService: สร้างข้อมูลไม่สำเร็จ:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * แก้ไขข้อมูลอ้างอิงผู้ใช้งาน (PATCH /reference-user-data/:id)
   * หมายเหตุ: payload จะไม่รวม idCardNumber เพราะไม่อนุญาตให้แก้ไขเลขบัตรประชาชน
   */
  updateReferenceUserData(
    id: string,
    payload: UpdateReferenceUserDataPayload
  ): Observable<ReferenceUserData> {
    const url = `${this.API_URL}/reference-user-data/${id}`;
    console.log('🔄 ReferenceUserDataService: กำลังแก้ไขข้อมูล...', id, payload);

    return this.http.patch<ReferenceUserData>(url, payload).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ ReferenceUserDataService: แก้ไขข้อมูลไม่สำเร็จ:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * กรองข้อมูลฝั่ง client ตามเงื่อนไขที่กำหนด
   */
  filterData(records: ReferenceUserData[], filters: ReferenceUserDataFilters): ReferenceUserData[] {
    if (!records || !Array.isArray(records)) {
      return [];
    }

    const normalize = (value: string) => value.replace(/\s+/g, '').toLowerCase();

    return records.filter(record => {
      if (!record) {
        return false;
      }

      // ค้นหาด้วยเลขบัตรประชาชน (ไม่สนใจช่องว่าง)
      if (filters.idCardSearch) {
        const target = normalize(record.idCardNumber || '');
        if (!target.includes(normalize(filters.idCardSearch))) {
          return false;
        }
      }

      // ค้นหาด้วยรหัสสมาชิก
      if (filters.memberIdSearch) {
        const target = (record.idMember || '').toLowerCase();
        if (!target.includes(filters.memberIdSearch.toLowerCase())) {
          return false;
        }
      }

      // กรองตามสถานะการสมัคร
      if (filters.registration && filters.registration !== 'all') {
        if (filters.registration === 'registered' && !record.hasRegistered) {
          return false;
        }
        if (filters.registration === 'not-registered' && record.hasRegistered) {
          return false;
        }
      }

      // กรองตามสถานะวันหมดอายุ
      if (filters.expiration && filters.expiration !== 'all') {
        const expiration = (record.expirationDate || '').trim();
        const isLifetime = expiration.includes('ตลอดชีพ');
        if (filters.expiration === 'lifetime' && !isLifetime) {
          return false;
        }
        if (filters.expiration === 'none' && expiration !== '') {
          return false;
        }
        if (filters.expiration === 'dated' && (expiration === '' || isLifetime)) {
          return false;
        }
      }

      // ค้นหาทั่วไป
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const fullName = `${record.title || ''} ${record.name || ''} ${record.lastname || ''}`.toLowerCase();
        const idCard = normalize(record.idCardNumber || '');
        const idMember = (record.idMember || '').toLowerCase();
        const normalizedTerm = normalize(filters.searchTerm);

        if (
          !fullName.includes(term) &&
          !idCard.includes(normalizedTerm) &&
          !idMember.includes(term)
        ) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Export ข้อมูลอ้างอิงเป็นไฟล์ CSV
   */
  exportToCSV(records: ReferenceUserData[]): void {
    const headers = [
      'คำนำหน้า',
      'ชื่อ',
      'นามสกุล',
      'เลขบัตรประชาชน',
      'รหัสสมาชิก',
      'วันหมดอายุ',
      'สถานะการสมัคร'
    ];

    const csvRows = records.map(record => [
      record.title || '',
      record.name || '',
      record.lastname || '',
      record.idCardNumber || '',
      record.idMember || '',
      record.expirationDate || '',
      record.hasRegistered ? 'สมัครแล้ว' : 'ยังไม่สมัคร'
    ]);

    const csvContent = [headers, ...csvRows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    // เพิ่ม BOM เพื่อให้ Excel อ่านภาษาไทยได้ถูกต้อง
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reference_user_data_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }
}
